// useAccountSession — the account-session read seam the shell's boot gate (05) consumes
// (contract 07 §9). It publishes the `SessionSnapshot` refs (all read-only to consumers) and the
// two sanctioned lifecycle entries: `boot()` (the shell's `useBootstrap` calls it) and `retry()`
// (the boot-error surface calls it). Token/RBAC live elsewhere — auth (06) owns token-only
// `useSession()`, and `useRbac()` owns authorization (contract §9/§18, ledger A4). This is NOT
// `useSession`: that name is auth's token seam; account's session composable is `useAccountSession`.
import { computed, type ComputedRef } from 'vue'

import { AccountBootstrapFlow, type AccountBootstrapHooks } from '../flows/AccountBootstrapFlow'
import {
  RetryDegradedSectionsFlow,
  type RetryDegradedOutcome,
} from '../flows/RetryDegradedSectionsFlow'
import type { UserResponse } from '../models/account.types'
import type { DegradedSection } from '../models/bootstrap.types'
import type { SessionStatus } from '../models/session.types'
import { useAccountStore } from '../stores/accountStore'
import { useSessionStore } from '../stores/sessionStore'
import { useWorkspaceStore } from '../stores/workspaceStore'

import type { ApiError } from '@/shared/http'

/** The reactive read-shape `useAccountSession()` exposes (the `SessionSnapshot` fields as refs) plus
 *  the boot/retry lifecycle entries. Refs are read-only — 07 is the single writer of `status`. */
export interface UseAccountSession {
  status: ComputedRef<SessionStatus>
  isReady: ComputedRef<boolean>
  isBooting: ComputedRef<boolean>
  /** DEGRADED READY (BE-ACC-06): `ready` while some sections stayed unresolved — the app runs,
   *  never an `error` and never a logout. `degradedSections` lists them with the server's
   *  `retryable` verdict (data-layer record; the retry surface is a later shell concern). */
  isDegraded: ComputedRef<boolean>
  bootError: ComputedRef<ApiError | null>
  degradedSections: ComputedRef<DegradedSection[]>
  user: ComputedRef<UserResponse | null>
  accountId: ComputedRef<number | undefined>
  workspaceId: ComputedRef<number | string | undefined>
  /** The one boot path. `reload=true` forces `refresh` on every section and bypasses the
   *  already-booted short-circuit (contract §8/§14). */
  boot: (reload?: boolean) => Promise<void>
  /** Re-run the boot after an error (contract §14 — retry, never an auto-logout loop). */
  retry: () => Promise<void>
  /** Re-ask ONLY for the retryable sections of a DEGRADED READY (ORP-20409). Unlike `retry()` this
   *  never re-boots: `status` stays `ready`, so there is no splash and no re-fetch of the sections
   *  that already landed. Returns the outcome — the caller owns the copy. */
  retryDegradedSections: () => Promise<RetryDegradedOutcome>
}

/** The shell's injection seam (07 stays feature-pure). Both entries are forwarded verbatim to
 *  every `AccountBootstrapFlow` this composable constructs. */
export interface UseAccountSessionOptions {
  /** Consumers for the post-boot hooks 07 owns the trigger for (contract §8). */
  hooks?: AccountBootstrapHooks
  /** Router-free eager-vs-deferred compositions seam. Omitted → the flow's `() => false`, i.e.
   *  compositions always load deferred. NOT a hook: the flow reads it during the fetch. */
  isCompositionsRoute?: () => boolean
}

export function useAccountSession(options: UseAccountSessionOptions = {}): UseAccountSession {
  const session = useSessionStore()
  const accountStore = useAccountStore()
  const workspaceStore = useWorkspaceStore()

  // A fresh flow per call is cheap: its constructor resolves the SAME singleton stores, so no state
  // is duplicated. The boot gate wires `onUnauthorized` to auth's logout and the composition root
  // supplies the rest (locale sync, package signal, no-account) — see `app/shell/
  // useAccountBootstrapHooks`. Both keys are spread conditionally: `exactOptionalPropertyTypes`
  // rejects an explicit `undefined`, and passing one would override the flow's own default.
  const boot = (reload = false): Promise<void> =>
    new AccountBootstrapFlow({
      ...(options.hooks ? { hooks: options.hooks } : {}),
      ...(options.isCompositionsRoute ? { isCompositionsRoute: options.isCompositionsRoute } : {}),
    }).execute(reload)

  return {
    status: computed(() => session.status),
    isReady: computed(() => session.isReady),
    isBooting: computed(() => session.isBooting),
    isDegraded: computed(() => session.isDegraded),
    bootError: computed(() => session.bootError),
    degradedSections: computed(() => session.degradedSections),
    user: computed(() => accountStore.user),
    accountId: computed(() => accountStore.accountId),
    workspaceId: computed(() => workspaceStore.workspaceId),
    boot,
    retry: () => boot(),
    // A one-shot command object per call, like the switch flows — it resolves the same singleton
    // stores, so nothing is duplicated.
    retryDegradedSections: () => new RetryDegradedSectionsFlow().execute(),
  }
}
